// ════════════════════════════════════════════════════════════════════
// i18n catalog — AdminAnalysis (Insight+) page.
//
// One entry per user-facing STATIC UI string rendered by the AdminAnalysis
// React component (pages/AdminAnalysis.js). Keys are namespaced "analysis.*"
// and are the stable contract the page references — do not rename without
// updating callers.
//
// SCOPE: only static UI copy is here — headings, labels, buttons, empty
// states, descriptions, chart/legend labels and the margin-band legend.
// Deliberately NOT translated (and so absent here):
//   • the styled PDF documents built by generateInsightsPDF/generatePricingPDF
//     (raw print-window HTML, not React-rendered app UI),
//   • the AI prompt strings sent to the model (instructions, not UI),
//   • all dynamic data, numbers, currency symbols, percentages, platform
//     names and brand names (Netrev / Stripe / Pro).
//
// The parent merges en/ar from this module into the main catalog in
// lib/i18n.js; the runtime resolution chain is ar → en → key, so a missing
// translation never blanks the UI.
// ════════════════════════════════════════════════════════════════════

export const en = {
  // ── Empty states ─────────────────────────────────────────────────────
  "analysis.empty.pickRange.title": "Pick a date range",
  "analysis.empty.pickRange.msg": "Select a from and to date in the filter above to view your P&L for any range.",
  "analysis.empty.noPeriodData.title": "No data for this period",
  // Mixed-content "no data for this period" message — split so dynamic
  // figures (entry count, days, range label) stay interpolated, not translated.
  "analysis.empty.noPeriodData.youHave": "You have ",
  "analysis.empty.noPeriodData.entriesSuffix": " entries",
  "analysis.empty.noPeriodData.butNoneIn": " in this workspace, but none in the ",
  "analysis.empty.noPeriodData.recentEntryWas": " Your most recent entry was ",
  "analysis.empty.noPeriodData.ago": " ago",
  "analysis.empty.noPeriodData.widenHint": " Use the date filter at the top to widen the range — try ",
  "analysis.empty.noPeriodData.allOption": "All",
  // SectionHdr sub suffix: "{workspace} · {range} · {n} log entries"
  "analysis.hdr.logEntriesSuffix": " log entries",
  "analysis.empty.noData.title": "No data yet",
  "analysis.empty.noData.msg": "Add daily log entries or import a CSV to see your P&L analysis. Once data is in, this page shows breakdowns, per-platform performance, and AI-powered insights.",

  // ── Period analysis card ─────────────────────────────────────────────
  "analysis.period.title": "Period analysis",
  "analysis.period.sub": "A plain-English read on this period's P&L, with specific actions.",
  "analysis.btn.downloadPdf": "Download PDF",
  "analysis.btn.analyzing": "Analyzing...",
  "analysis.btn.refresh": "Refresh",
  "analysis.btn.generateInsight": "Generate Insight",
  "analysis.btn.lockedInsight": "Subscribe to unlock",
  "analysis.btn.lockedInsightHint": "AI insights are included with a subscription. Your Daily Log and P&L keep working during the trial.",
  "analysis.loading.readingNumbers": "Reading your numbers...",
  "analysis.loading.usuallyTakes": "Usually takes 5-10 seconds",
  // "Generated {relative time} from current data" — split around dynamic time.
  "analysis.generatedPrefix": "Generated ",
  "analysis.period.generatedFromData": " from current data",
  // Empty-state hint inside the period card — split around inline <strong>
  // emphasis so wording stays accurate; "Generate Insight" reuses its button key.
  "analysis.period.hint.clickPrefix": "Click ",
  "analysis.period.hint.forTwoReports": " for two reports: a ",
  "analysis.period.hint.periodPnl": "period P&L",
  "analysis.period.hint.readMid": " read (verdict, biggest issue, actions, trends) and a ",
  "analysis.period.hint.menuPricing": "menu pricing",
  "analysis.period.hint.readSuffix": " read below (suggested prices, discount tiers, best platform per item). You can download the period report as a styled PDF.",

  // ── Menu pricing card ────────────────────────────────────────────────
  "analysis.pricing.title": "Menu pricing",
  "analysis.pricing.sub": "Suggested selling prices for a 10–15% net margin, your most profitable items, which to protect from discounting, and the best platform for each.",
  "analysis.loading.pricingMenu": "Pricing your menu...",
  "analysis.pricing.generatedFromMenu": " from current menu",
  // Empty-state hint inside the pricing card — split around inline <strong>.
  "analysis.pricing.hint.prefix": "Generated together with the period analysis above — click ",
  "analysis.pricing.hint.suffix": " for suggested prices, discount-tier breakdowns, and the best platform for each menu item.",

  // ── Top KPI labels ───────────────────────────────────────────────────
  "analysis.kpi.grossSales": "Gross sales",
  "analysis.kpi.netReceivable": "Net receivable",
  "analysis.kpi.trueProfit": "True profit",
  "analysis.kpi.profitMargin": "Profit margin",
  "analysis.kpi.info.vsDailyLog": "Differs from the Daily Log: Insight+ also deducts the subscription fee and VAT, so net receivable and true profit are lower here. (The Daily Log net receivable is before subscription.)",

  // ── Pie chart + breakdown ────────────────────────────────────────────
  // Pie title surrounds a dynamic currency+gross figure: "Where every {x} of gross went"
  "analysis.pie.titlePrefix": "Where every ",
  "analysis.pie.titleSuffix": " of gross went",
  "analysis.pie.grossLabel": "GROSS",
  // Pie / breakdown segment + row labels
  "analysis.seg.discountsGiven": "Discounts given",
  "analysis.seg.discountsYouFunded": "Discounts you funded",
  "analysis.seg.commission": "Commission",
  "analysis.seg.cardFees": "Card fees",
  "analysis.seg.cpcAdSpend": "CPC ad spend",
  "analysis.seg.vatOnFees": "VAT on fees",
  "analysis.seg.subscriptionFee": "Subscription fee",
  "analysis.seg.cogsFoodCost": "COGS (food cost)",
  "analysis.seg.trueProfit": "True profit",
  "analysis.seg.lossOver": "Loss (over)",

  // ── Detailed P&L breakdown table ─────────────────────────────────────
  "analysis.breakdown.title": "Detailed P&L breakdown",
  "analysis.row.grossSales": "Gross sales",
  "analysis.row.discountsGiven": "− Discounts given",
  "analysis.row.netExceedsGross": "⚠ Net exceeds gross by",
  "analysis.row.netSales": "= Net sales",
  "analysis.row.platformCoFund": "+ Platform contribution",
  "analysis.row.trueNetTicket": "= True net ticket",
  "analysis.row.aggregatorCommission": "− Aggregator commission",
  "analysis.row.vatOnCommission": "   ↳ VAT on commission",
  "analysis.row.cardFees": "− Card fees",
  "analysis.row.vatOnCardFees": "   ↳ VAT on card fees",
  "analysis.row.cpcAdSpend": "− CPC ad spend",
  "analysis.row.vatOnCpc": "   ↳ VAT on CPC",
  "analysis.row.campaignFees": "− Campaign fees",
  "analysis.row.subscriptionFee": "− Subscription fee",
  "analysis.row.vatOnSubscription": "   ↳ VAT on subscription",
  "analysis.row.netReceivable": "= Net receivable",
  "analysis.row.vat": "− VAT payable",
  "analysis.row.earningAfterVat": "= Earning after VAT",
  "analysis.row.cogsFoodCost": "− COGS (food cost)",
  "analysis.row.trueProfit": "= TRUE PROFIT",
  // Notes shown in the breakdown table's right-hand column
  "analysis.note.dataError": "data error — should be negative",
  "analysis.note.recoverable": "recoverable",
  // "X% of gross" / "X% on net receivable" notes — split so the figure stays dynamic
  "analysis.note.ofGrossSuffix": " of gross",
  "analysis.note.onNetReceivableSuffix": " on net receivable",
  "analysis.note.coFunded": "co-funded discounts",

  // ── Per-platform bar chart ───────────────────────────────────────────
  "analysis.bar.title": "Sales & profit by platform",
  "analysis.bar.sub": "Gross sales against true profit for each aggregator this period.",
  "analysis.bar.seriesGross": "Gross",
  "analysis.bar.seriesProfit": "Profit",

  // ── Per-platform performance table ───────────────────────────────────
  "analysis.platforms.titleSingle": "Platform breakdown",
  "analysis.platforms.titleMulti": "Per-platform performance",
  "analysis.platforms.subSingle": "Detailed metrics for this platform's contribution to the period.",
  "analysis.platforms.subMulti": "Each platform's true contribution after its own commission, fees, and CPC. Sorted by profit.",
  "analysis.platforms.noMatch": "No platforms match the column filters.",
  "analysis.platforms.badgeBest": "BEST",
  // Column headers (ppSortCols labels)
  "analysis.col.platform": "Platform",
  "analysis.col.entries": "Entries",
  "analysis.col.gross": "Gross",
  "analysis.col.net": "Net",
  "analysis.col.commission": "Commission",
  "analysis.col.cpc": "CPC",
  "analysis.col.cogs": "COGS",
  "analysis.col.trueProfit": "True Profit",
  "analysis.col.margin": "Margin",
  // Margin-band legend chips
  "analysis.legend.excellent": "Excellent",
  "analysis.legend.healthy": "Healthy",
  "analysis.legend.marginal": "Marginal",
  "analysis.legend.loss": "Loss",

  // ── Highlights ───────────────────────────────────────────────────────
  "analysis.highlights.title": "Highlights",
  "analysis.highlights.bestDay": "Best day",
  "analysis.highlights.weakestDay": "Weakest day",
  "analysis.highlights.topPlatform": "Top platform",
  "analysis.highlights.underperforming": "Underperforming",
  // Top-platform / underperforming sub-line: "X% margin · {y} profit"
  "analysis.highlights.marginSuffix": " margin",
  "analysis.highlights.profitSuffix": " profit",
  "analysis.highlights.losingMoney": "Losing money",
  "analysis.highlights.breakeven": "Breakeven",

  // ── Rule-based flags (static title + body; figures stay interpolated) ─
  "analysis.flags.headingPrefix": "Flags",
  "analysis.flags.cpcHigh.title": "CPC spend is high",
  "analysis.flags.cpcHigh.text.prefix": "You're spending ",
  "analysis.flags.cpcHigh.text.suffix": " of net sales on CPC ads. Industry average is 3–6%. Consider reducing campaign budgets to grow profit.",
  "analysis.flags.cpcHigh.text.suffixCoFund": " of your true net ticket (net sales + platform contribution) on CPC ads. Industry average is 3–6%. Consider reducing campaign budgets to grow profit.",
  "analysis.flags.heavyDiscount.title": "Heavy discounting",
  "analysis.flags.heavyDiscount.text.prefix": "Average discount is ",
  "analysis.flags.heavyDiscount.text.suffix": " of gross. Frequent promos can erode margins. Ensure each promo drives enough new orders to offset the cost.",
  "analysis.flags.heavyDiscount.text.prefixOwn": "The discount you fund (after platform co-funding) averages ",
  "analysis.flags.thinMargin.title": "Thin net margin",
  "analysis.flags.thinMargin.text.prefix": "Net margin is ",
  "analysis.flags.thinMargin.text.suffix": " of gross — genuinely thin even for a delivery model. Little buffer if commission, COGS or ad costs rise.",
  "analysis.flags.cardFeesHigh.title": "Card fees above standard",
  "analysis.flags.cardFeesHigh.text.prefix": "Card fees are ",
  "analysis.flags.cardFeesHigh.text.suffix": " of net sales. Standard is ~2.5%. Worth reviewing your payment processing agreement.",
  "analysis.flags.cardFeesHigh.text.suffixCoFund": " of your true net ticket (net sales + platform contribution). Standard is ~2.5%. Worth reviewing your payment processing agreement.",

  // ── Load warnings ────────────────────────────────────────────────────
  // Shown when the platform-contribution (co-funding) entries failed to load.
  "analysis.warn.coFundLoadFailed": "Platform contributions couldn't be loaded, so these figures don't include them. Refresh the page to try again.",
};

export const ar = {
  // ── Empty states ─────────────────────────────────────────────────────
  "analysis.empty.pickRange.title": "اختر نطاقًا زمنيًا",
  "analysis.empty.pickRange.msg": "حدّد تاريخ البداية والنهاية في عامل التصفية أعلاه لعرض الأرباح والخسائر لأي نطاق.",
  "analysis.empty.noPeriodData.title": "لا توجد بيانات لهذه الفترة",
  "analysis.empty.noPeriodData.youHave": "لديك ",
  "analysis.empty.noPeriodData.entriesSuffix": " إدخالات",
  "analysis.empty.noPeriodData.butNoneIn": " في مساحة العمل هذه، لكن لا يوجد أي منها في ",
  "analysis.empty.noPeriodData.recentEntryWas": " كان أحدث إدخال لديك منذ ",
  "analysis.empty.noPeriodData.ago": "",
  "analysis.empty.noPeriodData.widenHint": " استخدم عامل تصفية التاريخ في الأعلى لتوسيع النطاق — جرّب ",
  "analysis.empty.noPeriodData.allOption": "الكل",
  "analysis.hdr.logEntriesSuffix": " إدخالات السجل",
  "analysis.empty.noData.title": "لا توجد بيانات بعد",
  "analysis.empty.noData.msg": "أضف إدخالات السجل اليومي أو استورد ملف CSV لرؤية تحليل الأرباح والخسائر. بمجرد توفّر البيانات، تعرض هذه الصفحة التفصيلات والأداء لكل منصة ورؤى مدعومة بالذكاء الاصطناعي.",

  // ── Period analysis card ─────────────────────────────────────────────
  "analysis.period.title": "تحليل الفترة",
  "analysis.period.sub": "قراءة واضحة بلغة بسيطة للأرباح والخسائر في هذه الفترة، مع إجراءات محدّدة.",
  "analysis.btn.downloadPdf": "تنزيل PDF",
  "analysis.btn.analyzing": "جارٍ التحليل...",
  "analysis.btn.refresh": "تحديث",
  "analysis.btn.generateInsight": "إنشاء تحليل",
  "analysis.btn.lockedInsight": "اشترك لفتح الميزة",
  "analysis.btn.lockedInsightHint": "تحليلات الذكاء الاصطناعي مشمولة مع الاشتراك. يستمر السجل اليومي وحساب الأرباح خلال الفترة التجريبية.",
  "analysis.loading.readingNumbers": "جارٍ قراءة أرقامك...",
  "analysis.loading.usuallyTakes": "يستغرق عادةً من 5 إلى 10 ثوانٍ",
  "analysis.generatedPrefix": "أُنشئ ",
  "analysis.period.generatedFromData": " من البيانات الحالية",
  "analysis.period.hint.clickPrefix": "انقر على ",
  "analysis.period.hint.forTwoReports": " للحصول على تقريرين: قراءة ",
  "analysis.period.hint.periodPnl": "أرباح وخسائر الفترة",
  "analysis.period.hint.readMid": " (الحُكم، أكبر مشكلة، الإجراءات، الاتجاهات) و قراءة ",
  "analysis.period.hint.menuPricing": "تسعير القائمة",
  "analysis.period.hint.readSuffix": " أدناه (الأسعار المقترحة، شرائح الخصم، أفضل منصة لكل صنف). يمكنك تنزيل تقرير الفترة كملف PDF منسّق.",

  // ── Menu pricing card ────────────────────────────────────────────────
  "analysis.pricing.title": "تسعير القائمة",
  "analysis.pricing.sub": "أسعار البيع المقترحة لتحقيق هامش صافٍ من 10 إلى 15%، وأكثر أصنافك ربحية، وأيّها ينبغي حمايته من الخصم، وأفضل منصة لكل صنف.",
  "analysis.loading.pricingMenu": "جارٍ تسعير قائمتك...",
  "analysis.pricing.generatedFromMenu": " من القائمة الحالية",
  "analysis.pricing.hint.prefix": "يُنشأ مع تحليل الفترة أعلاه — انقر على ",
  "analysis.pricing.hint.suffix": " للحصول على الأسعار المقترحة وتفصيلات شرائح الخصم وأفضل منصة لكل صنف في القائمة.",

  // ── Top KPI labels ───────────────────────────────────────────────────
  "analysis.kpi.grossSales": "إجمالي المبيعات",
  "analysis.kpi.netReceivable": "صافي المستحقات",
  "analysis.kpi.trueProfit": "الربح الحقيقي",
  "analysis.kpi.profitMargin": "هامش الربح",
  "analysis.kpi.info.vsDailyLog": "تختلف عن السجل اليومي: يخصم Insight+ أيضًا رسوم الاشتراك وضريبة القيمة المضافة، لذا يكون صافي المستحقات والربح الحقيقي أقل هنا. (صافي المستحقات في السجل اليومي قبل الاشتراك.)",

  // ── Pie chart + breakdown ────────────────────────────────────────────
  "analysis.pie.titlePrefix": "إلى أين ذهب كل ",
  "analysis.pie.titleSuffix": " من الإجمالي",
  "analysis.pie.grossLabel": "الإجمالي",
  "analysis.seg.discountsGiven": "الخصومات الممنوحة",
  "analysis.seg.discountsYouFunded": "الخصومات التي موّلتها",
  "analysis.seg.commission": "العمولة",
  "analysis.seg.cardFees": "رسوم البطاقة",
  "analysis.seg.cpcAdSpend": "إنفاق إعلانات CPC",
  "analysis.seg.vatOnFees": "ضريبة القيمة المضافة على الرسوم",
  "analysis.seg.subscriptionFee": "رسوم الاشتراك",
  "analysis.seg.cogsFoodCost": "تكلفة البضاعة المباعة (تكلفة الطعام)",
  "analysis.seg.trueProfit": "الربح الحقيقي",
  "analysis.seg.lossOver": "الخسارة (الزائدة)",

  // ── Detailed P&L breakdown table ─────────────────────────────────────
  "analysis.breakdown.title": "تفصيل الأرباح والخسائر",
  "analysis.row.grossSales": "إجمالي المبيعات",
  "analysis.row.discountsGiven": "− الخصومات الممنوحة",
  "analysis.row.netExceedsGross": "⚠ الصافي يتجاوز الإجمالي بمقدار",
  "analysis.row.netSales": "= صافي المبيعات",
  "analysis.row.platformCoFund": "+ مساهمة المنصة",
  "analysis.row.trueNetTicket": "= صافي الطلب الحقيقي",
  "analysis.row.aggregatorCommission": "− عمولة المنصة",
  "analysis.row.vatOnCommission": "   ↳ ضريبة القيمة المضافة على العمولة",
  "analysis.row.cardFees": "− رسوم البطاقة",
  "analysis.row.vatOnCardFees": "   ↳ ضريبة القيمة المضافة على رسوم البطاقة",
  "analysis.row.cpcAdSpend": "− إنفاق إعلانات CPC",
  "analysis.row.vatOnCpc": "   ↳ ضريبة القيمة المضافة على CPC",
  "analysis.row.campaignFees": "− رسوم الحملات",
  "analysis.row.subscriptionFee": "− رسوم الاشتراك",
  "analysis.row.vatOnSubscription": "   ↳ ضريبة القيمة المضافة على الاشتراك",
  "analysis.row.netReceivable": "= صافي المستحقات",
  "analysis.row.vat": "− ضريبة القيمة المضافة المستحقة",
  "analysis.row.earningAfterVat": "= الأرباح بعد ضريبة القيمة المضافة",
  "analysis.row.cogsFoodCost": "− تكلفة البضاعة المباعة (تكلفة الطعام)",
  "analysis.row.trueProfit": "= الربح الحقيقي",
  "analysis.note.dataError": "خطأ في البيانات — يجب أن يكون سالبًا",
  "analysis.note.recoverable": "قابلة للاسترداد",
  "analysis.note.ofGrossSuffix": " من الإجمالي",
  "analysis.note.onNetReceivableSuffix": " على صافي المستحقات",
  "analysis.note.coFunded": "خصومات بتمويل مشترك",

  // ── Per-platform bar chart ───────────────────────────────────────────
  "analysis.bar.title": "المبيعات والربح حسب المنصة",
  "analysis.bar.sub": "إجمالي المبيعات مقابل الربح الحقيقي لكل منصة في هذه الفترة.",
  "analysis.bar.seriesGross": "الإجمالي",
  "analysis.bar.seriesProfit": "الربح",

  // ── Per-platform performance table ───────────────────────────────────
  "analysis.platforms.titleSingle": "تفصيل المنصة",
  "analysis.platforms.titleMulti": "الأداء حسب كل منصة",
  "analysis.platforms.subSingle": "مقاييس تفصيلية لمساهمة هذه المنصة في الفترة.",
  "analysis.platforms.subMulti": "المساهمة الحقيقية لكل منصة بعد عمولتها ورسومها وإنفاق CPC الخاص بها. مرتّبة حسب الربح.",
  "analysis.platforms.noMatch": "لا توجد منصات تطابق عوامل تصفية الأعمدة.",
  "analysis.platforms.badgeBest": "الأفضل",
  "analysis.col.platform": "المنصة",
  "analysis.col.entries": "الإدخالات",
  "analysis.col.gross": "الإجمالي",
  "analysis.col.net": "الصافي",
  "analysis.col.commission": "العمولة",
  "analysis.col.cpc": "CPC",
  "analysis.col.cogs": "تكلفة البضاعة المباعة",
  "analysis.col.trueProfit": "الربح الحقيقي",
  "analysis.col.margin": "الهامش",
  "analysis.legend.excellent": "ممتاز",
  "analysis.legend.healthy": "جيد",
  "analysis.legend.marginal": "هامشي",
  "analysis.legend.loss": "خسارة",

  // ── Highlights ───────────────────────────────────────────────────────
  "analysis.highlights.title": "أبرز النقاط",
  "analysis.highlights.bestDay": "أفضل يوم",
  "analysis.highlights.weakestDay": "أضعف يوم",
  "analysis.highlights.topPlatform": "أفضل منصة",
  "analysis.highlights.underperforming": "منخفض الأداء",
  "analysis.highlights.marginSuffix": " هامش",
  "analysis.highlights.profitSuffix": " ربح",
  "analysis.highlights.losingMoney": "تحقّق خسارة",
  "analysis.highlights.breakeven": "نقطة التعادل",

  // ── Rule-based flags ─────────────────────────────────────────────────
  "analysis.flags.headingPrefix": "تنبيهات",
  "analysis.flags.cpcHigh.title": "إنفاق CPC مرتفع",
  "analysis.flags.cpcHigh.text.prefix": "أنت تنفق ",
  "analysis.flags.cpcHigh.text.suffix": " من صافي المبيعات على إعلانات CPC. متوسط القطاع هو 3–6%. فكّر في تقليل ميزانيات الحملات لزيادة الربح.",
  "analysis.flags.cpcHigh.text.suffixCoFund": " من صافي طلبك الحقيقي (صافي المبيعات + مساهمة المنصة) على إعلانات CPC. متوسط القطاع هو 3–6%. فكّر في تقليل ميزانيات الحملات لزيادة الربح.",
  "analysis.flags.heavyDiscount.title": "خصم مفرط",
  "analysis.flags.heavyDiscount.text.prefix": "متوسط الخصم هو ",
  "analysis.flags.heavyDiscount.text.suffix": " من الإجمالي. العروض المتكررة قد تتآكل بها الهوامش. تأكّد من أن كل عرض يجلب طلبات جديدة كافية لتعويض التكلفة.",
  "analysis.flags.heavyDiscount.text.prefixOwn": "متوسط الخصم الذي تموّله (بعد التمويل المشترك من المنصة) هو ",
  "analysis.flags.thinMargin.title": "هامش صافٍ ضعيف",
  "analysis.flags.thinMargin.text.prefix": "الهامش الصافي هو ",
  "analysis.flags.thinMargin.text.suffix": " من الإجمالي — ضعيف فعليًا حتى بالنسبة لنموذج التوصيل. هامش أمان ضئيل في حال ارتفاع العمولة أو تكلفة البضاعة أو تكاليف الإعلانات.",
  "analysis.flags.cardFeesHigh.title": "رسوم البطاقة أعلى من المعتاد",
  "analysis.flags.cardFeesHigh.text.prefix": "رسوم البطاقة هي ",
  "analysis.flags.cardFeesHigh.text.suffix": " من صافي المبيعات. المعتاد هو ~2.5%. يستحق مراجعة اتفاقية معالجة المدفوعات لديك.",
  "analysis.flags.cardFeesHigh.text.suffixCoFund": " من صافي طلبك الحقيقي (صافي المبيعات + مساهمة المنصة). المعتاد هو ~2.5%. يستحق مراجعة اتفاقية معالجة المدفوعات لديك.",

  // ── Load warnings ────────────────────────────────────────────────────
  "analysis.warn.coFundLoadFailed": "تعذّر تحميل مساهمات المنصة، لذا لا تشملها هذه الأرقام. حدّث الصفحة للمحاولة مرة أخرى.",
};
